import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  MessageCircle, Settings, Send, CheckCircle2, Clock, AlertCircle,
  Loader2, Save, Phone, RefreshCw, BarChart3, Eye, ChevronRight
} from 'lucide-react';

const TEMPLATES = [
  { key: 'confirmation', label: 'Confirmación de reserva', icon: '✅', desc: 'Enviado al confirmar una reserva', delay: 'Inmediato' },
  { key: 'reminder_24h', label: 'Recordatorio 24h antes', icon: '⏰', desc: 'Recordatorio el día anterior', delay: '24h antes' },
  { key: 'reminder_2h', label: 'Recordatorio 2h antes', icon: '🔔', desc: 'Recordatorio pocas horas antes', delay: '2h antes' },
  { key: 'post_visit', label: 'Post-visita', icon: '⭐', desc: 'Solicitud de feedback después de la visita', delay: '2h después' },
  { key: 'waitlist', label: 'Lista de espera', icon: '📋', desc: 'Notificación cuando hay disponibilidad', delay: 'Inmediato' },
  { key: 'cancellation', label: 'Cancelación', icon: '❌', desc: 'Confirmación de cancelación', delay: 'Inmediato' },
];

const SAMPLE_MESSAGES = {
  confirmation: '¡Hola {{nombre}}! 👋 Tu reserva en *{{restaurante}}* está confirmada.\n\n📅 *Fecha:* {{fecha}}\n⏰ *Hora:* {{hora}}\n👥 *Personas:* {{personas}}\n\n¡Te esperamos! Si necesitas cambiar o cancelar, responde a este mensaje.',
  reminder_24h: '¡Hola {{nombre}}! ⏰ Te recordamos que mañana tienes reserva en *{{restaurante}}*.\n\n📅 {{fecha}} a las {{hora}}\n👥 {{personas}} personas\n\n¿Confirmas tu asistencia? Responde SI o NO.',
  reminder_2h: '¡{{nombre}}, te esperamos hoy! 🎉 Tu mesa en *{{restaurante}}* está lista para las {{hora}}.\n\n📍 Nos vemos en breve.',
  post_visit: '¡Gracias por visitarnos, {{nombre}}! 🙏 Esperamos que tu experiencia en *{{restaurante}}* haya sido excelente.\n\n¿Cómo calificarías tu visita del 1 al 10?\n\nTu opinión nos ayuda a mejorar. 💙',
  waitlist: '¡Buenas noticias, {{nombre}}! 🎊 Ha quedado disponible una mesa en *{{restaurante}}*.\n\n⏰ {{hora}} · 👥 {{personas}} personas\n\nResponde SI para confirmar (válido 15 minutos).',
  cancellation: 'Hola {{nombre}}, tu reserva en *{{restaurante}}* del {{fecha}} a las {{hora}} ha sido cancelada.\n\n¡Esperamos verte pronto! 🙌',
};

export default function WhatsAppNotifications() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState({ total: 0, delivered: 0, opened: 0, delivery_rate: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState('confirmation');
  const [tab, setTab] = useState('config');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, statsRes, histRes] = await Promise.all([
        api.get('/whatsapp/config'),
        api.get('/whatsapp/stats'),
        api.get('/whatsapp/history'),
      ]);
      setConfig(cfgRes.data);
      setStats(statsRes.data);
      setHistory(histRes.data || []);
    } catch { toast.error('Error al cargar configuración'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/whatsapp/config', config);
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const setTemplate = (key, val) => setConfig(c => ({ ...c, templates: { ...c.templates, [key]: val } }));

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="WhatsApp" helpSection="whatsapp" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="WhatsApp" helpSection="whatsapp" actions={
        <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Guardar
        </Button>
      } />

      <div className="flex-1 p-6 max-w-6xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-green-500 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">WhatsApp Business</h1>
            <p className="text-slate-500 text-sm">Notificaciones automáticas por WhatsApp para tus clientes</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className={cn("w-2.5 h-2.5 rounded-full", config?.enabled ? "bg-green-500 animate-pulse" : "bg-slate-300")} />
            <span className="text-sm font-medium text-slate-600">{config?.enabled ? 'Activo' : 'Inactivo'}</span>
            <Switch checked={!!config?.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} className="ml-1" />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { l: 'Mensajes enviados', v: stats.total, icon: Send, color: 'text-blue-600', bg: 'bg-blue-50' },
            { l: 'Entregados', v: stats.delivered, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Leídos', v: stats.opened, icon: Eye, color: 'text-purple-600', bg: 'bg-purple-50' },
            { l: 'Tasa de entrega', v: `${stats.delivery_rate}%`, icon: BarChart3, color: 'text-orange-600', bg: 'bg-orange-50' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-4.5 w-4.5", s.color)} />
                </div>
                <div className="text-2xl font-bold text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[{ k: 'config', l: 'Configuración' }, { k: 'templates', l: 'Plantillas' }, { k: 'history', l: 'Historial' }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all",
                tab === t.k ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700")}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Config Tab */}
        {tab === 'config' && (
          <div className="grid grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Phone className="h-4 w-4 text-green-600" /> Conexión vía Kapso AI</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs text-green-800">
                  <strong>✅ Integración Kapso:</strong> OkReserva usa Kapso AI para enviar notificaciones WhatsApp de forma automática. Tu restaurante ya está conectado a través de la plataforma.
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <p className="text-xs font-semibold text-slate-600">Plataforma Kapso</p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-xs text-slate-600">API: <code className="text-green-700 font-mono">api.kapso.ai/platform/v1</code></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-xs text-slate-600">WhatsApp Meta: <code className="text-green-700 font-mono">api.kapso.ai/meta/whatsapp</code></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={cn("w-2 h-2 rounded-full", config?.enabled ? "bg-green-500 animate-pulse" : "bg-slate-300")} />
                    <span className="text-xs text-slate-600">Estado: <strong className={config?.enabled ? "text-green-700" : "text-slate-500"}>{config?.enabled ? 'Activo' : 'Inactivo'}</strong></span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Número WhatsApp Business (para referencia)</Label>
                  <Input value={config?.phone_number_id || ''} onChange={e => setConfig(c => ({ ...c, phone_number_id: e.target.value }))}
                    placeholder="+52 55 0000 0000" className="text-sm" />
                </div>
                <div className="pt-2 border-t">
                  <p className="text-xs text-slate-400 mb-2">Gestión de API Key</p>
                  <div className="p-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                    La API Key de Kapso es administrada por OkReserva. Para cambios en tu integración, contacta a soporte.
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Settings className="h-4 w-4 text-slate-500" /> Notificaciones activas</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {TEMPLATES.map(t => (
                  <div key={t.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{t.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-slate-700">{t.label}</p>
                        <p className="text-[11px] text-slate-400">{t.delay}</p>
                      </div>
                    </div>
                    <Switch checked={!!config?.templates?.[t.key]}
                      onCheckedChange={v => setTemplate(t.key, v)} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Templates Tab */}
        {tab === 'templates' && (
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              {TEMPLATES.map(t => (
                <button key={t.key} onClick={() => setActiveTemplate(t.key)}
                  className={cn("w-full text-left p-3 rounded-xl border transition-all",
                    activeTemplate === t.key ? "border-green-500 bg-green-50" : "border-slate-200 bg-white hover:border-slate-300")}>
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">{t.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{t.label}</p>
                      <p className="text-[10px] text-slate-400">{t.delay}</p>
                    </div>
                    <ChevronRight className={cn("h-4 w-4 flex-shrink-0 transition-colors", activeTemplate === t.key ? "text-green-600" : "text-slate-300")} />
                  </div>
                </button>
              ))}
            </div>
            <div className="col-span-2">
              <Card className="border-0 shadow-sm h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <span className="text-xl">{TEMPLATES.find(t => t.key === activeTemplate)?.icon}</span>
                    {TEMPLATES.find(t => t.key === activeTemplate)?.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-[#e5ddd5] rounded-xl p-4" style={{ backgroundImage: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQImWNgYGBgAAAABQABh6FO1AAAAABJRU5ErkJggg==)' }}>
                    <div className="bg-white rounded-xl rounded-tl-none p-3 max-w-[85%] shadow-sm">
                      <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">
                        {SAMPLE_MESSAGES[activeTemplate]}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-2 text-right">10:30 ✓✓</p>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl text-xs text-slate-600">
                    <strong>Variables disponibles:</strong>{' '}
                    {'{{nombre}} {{restaurante}} {{fecha}} {{hora}} {{personas}}'}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {history.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sin historial de mensajes</p>
                  <p className="text-sm mt-1">Los mensajes enviados aparecerán aquí</p>
                </div>
              ) : (
                <div className="divide-y">
                  {history.map((h, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                      <div className={cn("w-2 h-2 rounded-full flex-shrink-0",
                        h.status === 'read' ? 'bg-blue-500' : h.status === 'delivered' ? 'bg-green-500' : 'bg-slate-300')} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{h.to || 'Cliente'}</p>
                        <p className="text-xs text-slate-400 truncate">{h.template}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-medium text-slate-600">{h.status}</p>
                        <p className="text-[10px] text-slate-400">{h.sent_at ? new Date(h.sent_at).toLocaleDateString('es-ES') : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
